import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  makeStyles,
  List,
  Button,
  Tooltip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Menu,
  Tabs,
  Tab,
  CircularProgress,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SortIcon from '@material-ui/icons/Sort';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import BusinessIcon from '@material-ui/icons/Business';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import FolderSpecialIcon from '@material-ui/icons/FolderSpecial';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import { Alert } from '@material-ui/lab';
import { useApi, googleAuthApiRef } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { homepageGcpProjectsApiRef, homepageLinksApiRef } from '../../api';
import {
  HomepageGcpProject,
  GcpProjectSortField,
  SortDirection,
  ProjectBillingInfo,
  CreateGcpProjectInput,
  GcpProjectsViewMode,
  GcpOrganization,
  OrgProject,
} from '../../types';
import { CollapsibleSection } from '../CollapsibleSection';
import { GcpProjectItem } from '../GcpProjectItem';
import { GcpProjectContributionDialog } from '../GcpProjectContributionDialog';
import { OrgProjectBrowserDialog } from '../OrgProjectBrowserDialog';
import { EmptyState } from '../EmptyState';
import { ReorderDialog } from '../ReorderDialog';
import { GcpHierarchyView } from '../GcpHierarchyView';

const useStyles = makeStyles(theme => ({
  addButton: {
    marginLeft: theme.spacing(1),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  sortControl: {
    minWidth: 150,
  },
  orgControl: {
    minWidth: 200,
  },
  searchField: {
    minWidth: 200,
  },
  viewModeTabs: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tab: {
    minWidth: 120,
  },
  orgSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  orgInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  loadingOrgProjects: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
}));

export interface GcpProjectsSectionProps {
  defaultCollapsed?: boolean;
  editMode?: boolean;
  onHide?: () => void;
  /** Filter projects to only show those associated with selected group refs */
  selectedGroupRefs?: string[];
  /** Whether the user chip is selected (show user-specific projects) */
  userSelected?: boolean;
}

export const GcpProjectsSection = ({
  defaultCollapsed = false,
  editMode = false,
  onHide,
  selectedGroupRefs,
  userSelected = true,
}: GcpProjectsSectionProps) => {
  const classes = useStyles();
  const gcpProjectsApi = useApi(homepageGcpProjectsApiRef);
  const linksApi = useApi(homepageLinksApiRef);
  const googleAuthApi = useApi(googleAuthApiRef);

  // Saved projects state
  const [projects, setProjects] = useState<HomepageGcpProject[]>([]);
  const [jwtGroups, setJwtGroups] = useState<string[]>([]);
  const [userRef, setUserRef] = useState<string | undefined>();
  const [groupOrder, setGroupOrder] = useState<string[] | undefined>();

  // Merge JWT-based groups with GitHub team refs from the teams bar so components
  // are aware of the user's groups even when the JWT ent claim is empty.
  const userGroups = useMemo(() => {
    const merged = new Set([...jwtGroups, ...(selectedGroupRefs || [])]);
    return Array.from(merged);
  }, [jwtGroups, selectedGroupRefs]);
  const [loading, setLoading] = useState(true);
  const [isHidden, setIsHidden] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<HomepageGcpProject | null>(null);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [orgBrowserOpen, setOrgBrowserOpen] = useState(false);
  const [hiddenProjects, setHiddenProjects] = useState<Set<string>>(new Set());
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  // View mode state
  const [viewMode, setViewMode] = useState<GcpProjectsViewMode>('saved');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  // Google auth state for org mode
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [needsGoogleAuth, setNeedsGoogleAuth] = useState(false);

  // Organization data
  const [organizations, setOrganizations] = useState<GcpOrganization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [orgProjects, setOrgProjects] = useState<OrgProject[]>([]);
  const [loadingOrgProjects, setLoadingOrgProjects] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<GcpProjectSortField>('projectId');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Billing data
  const [billingData, setBillingData] = useState<Record<string, ProjectBillingInfo>>({});
  const [loadingBilling, setLoadingBilling] = useState(false);

  // Search filter
  const [searchQuery, setSearchQuery] = useState('');

  // Load saved projects and preferences
  const loadData = useCallback(async (accessToken?: string | null) => {
    try {
      const [projectsData, userInfo, prefs] = await Promise.all([
        gcpProjectsApi.getProjects(selectedGroupRefs),
        gcpProjectsApi.getUserInfo(),
        linksApi.getPreferences(),
      ]);
      setProjects(projectsData);
      setJwtGroups(userInfo.groups);
      setUserRef(userInfo.userRef);
      setGroupOrder(prefs.groupOrder);
      setIsHidden(prefs.hiddenSections?.includes('gcp-projects') ?? false);

      // Restore view mode preferences
      if (prefs.gcpProjectsViewMode) {
        setViewMode(prefs.gcpProjectsViewMode);
      }
      if (prefs.gcpProjectsSelectedOrg) {
        setSelectedOrgId(prefs.gcpProjectsSelectedOrg);
      }

      // Load billing data for saved projects (requires Google token)
      if (projectsData.length > 0 && accessToken) {
        setLoadingBilling(true);
        try {
          const projectIds = projectsData.map(p => p.projectId);
          const billing = await gcpProjectsApi.getProjectsBilling(projectIds, accessToken);
          setBillingData(billing);
        } catch (billingErr) {
          console.error('Failed to load billing data:', billingErr);
        } finally {
          setLoadingBilling(false);
        }
      }
    } catch (err) {
      console.error('Failed to load GCP projects:', err);
    } finally {
      setLoading(false);
    }
  }, [gcpProjectsApi, linksApi, selectedGroupRefs]);

  useEffect(() => {
    loadData(googleToken);
  }, [loadData, googleToken]);

  // Check for existing Google token on mount (needed for billing and org mode)
  useEffect(() => {
    const checkGoogleAuth = async () => {
      try {
        const token = await googleAuthApi.getAccessToken(
          ['https://www.googleapis.com/auth/cloud-platform.read-only'],
          { optional: true },
        );
        if (token) {
          setGoogleToken(token);
          setNeedsGoogleAuth(false);
        } else if (viewMode === 'organization') {
          setNeedsGoogleAuth(true);
        }
      } catch {
        if (viewMode === 'organization') {
          setNeedsGoogleAuth(true);
        }
      }
    };

    checkGoogleAuth();
  }, [viewMode, googleAuthApi]);

  // Load organizations when we have a token
  useEffect(() => {
    if (!googleToken || viewMode !== 'organization') return;

    const loadOrgs = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await gcpProjectsApi.getOrganizations(googleToken);
        setOrganizations(orgs);
        // Auto-select first org if none selected
        if (orgs.length > 0 && !selectedOrgId) {
          setSelectedOrgId(orgs[0].name);
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrgs();
  }, [googleToken, viewMode, gcpProjectsApi, selectedOrgId]);

  // Load org projects when org is selected
  useEffect(() => {
    if (!googleToken || !selectedOrgId || viewMode !== 'organization') return;

    const loadOrgProjects = async () => {
      setLoadingOrgProjects(true);
      setOrgProjects([]);
      try {
        const orgProjectsData = await gcpProjectsApi.getOrgProjects(googleToken, selectedOrgId);
        setOrgProjects(orgProjectsData);

        // Load billing for org projects (we already have googleToken in this flow)
        if (orgProjectsData.length > 0 && googleToken) {
          setLoadingBilling(true);
          try {
            const projectIds = orgProjectsData.map(p => p.projectId);
            const billing = await gcpProjectsApi.getProjectsBilling(projectIds, googleToken);
            setBillingData(prev => ({ ...prev, ...billing }));
          } catch (billingErr) {
            console.error('Failed to load billing data for org projects:', billingErr);
          } finally {
            setLoadingBilling(false);
          }
        }
      } catch (err) {
        console.error('Failed to load org projects:', err);
      } finally {
        setLoadingOrgProjects(false);
      }
    };

    loadOrgProjects();
  }, [googleToken, selectedOrgId, viewMode, gcpProjectsApi]);

  // Save view mode preference when it changes
  const handleViewModeChange = useCallback(async (_event: React.ChangeEvent<{}>, newValue: GcpProjectsViewMode) => {
    setViewMode(newValue);
    await linksApi.updatePreferences({ gcpProjectsViewMode: newValue });
  }, [linksApi]);

  // Save selected org preference when it changes
  const handleOrgChange = useCallback(async (orgId: string) => {
    setSelectedOrgId(orgId);
    await linksApi.updatePreferences({ gcpProjectsSelectedOrg: orgId });
  }, [linksApi]);

  const handleGoogleSignIn = useCallback(async () => {
    try {
      const token = await googleAuthApi.getAccessToken([
        'https://www.googleapis.com/auth/cloud-platform.read-only',
      ]);
      if (token) {
        setGoogleToken(token);
        setNeedsGoogleAuth(false);
      }
    } catch (err) {
      console.error('Google sign-in failed:', err);
    }
  }, [googleAuthApi]);

  const handleAddProject = useCallback(async (input: any) => {
    await gcpProjectsApi.createProject(input);
    await loadData(googleToken);
  }, [gcpProjectsApi, loadData, googleToken]);

  const handleAddProjectsFromOrg = useCallback(async (inputs: CreateGcpProjectInput[]) => {
    for (const input of inputs) {
      try {
        await gcpProjectsApi.createProject(input);
      } catch (err) {
        console.warn(`Failed to add project ${input.projectId}:`, err);
      }
    }
    await loadData(googleToken);
  }, [gcpProjectsApi, loadData, googleToken]);

  const handleDeleteProject = useCallback(
    async (projectId: string) => {
      await gcpProjectsApi.deleteProject(projectId);
      await loadData(googleToken);
      setEditingProject(null);
    },
    [gcpProjectsApi, loadData, googleToken],
  );

  const handleHideSection = useCallback(async () => {
    const prefs = await linksApi.getPreferences();
    const currentHidden = prefs.hiddenSections ?? [];
    if (!currentHidden.includes('gcp-projects')) {
      await linksApi.updatePreferences({
        hiddenSections: [...currentHidden, 'gcp-projects'],
      });
      setIsHidden(true);
      onHide?.();
    }
  }, [linksApi, onHide]);

  const handleSaveGroupOrder = useCallback(async (newOrder: string[]) => {
    await linksApi.updatePreferences({ groupOrder: newOrder });
    setGroupOrder(newOrder);
  }, [linksApi]);

  const handleHideProject = useCallback((project: HomepageGcpProject) => {
    setHiddenProjects(prev => new Set(prev).add(project.id));
  }, []);

  const handleEditGroups = useCallback((project: HomepageGcpProject) => {
    setEditingProject(project);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingProject(null);
  }, []);

  // Get groups that actually have projects in this section
  const groupsWithProjects = useMemo(() => {
    const allGroupsInProjects = new Set<string>();
    projects.forEach(project => {
      project.groups.forEach(g => {
        if (userGroups.includes(g) || g === userRef) {
          allGroupsInProjects.add(g);
        }
      });
    });
    return userGroups.filter(g => allGroupsInProjects.has(g));
  }, [projects, userGroups, userRef]);

  const hasPersonalProjects = useMemo(() => {
    return userRef ? projects.some(project => project.groups.includes(userRef)) : false;
  }, [projects, userRef]);

  // Convert org projects to HomepageGcpProject format for display
  const orgProjectsAsHomepage = useMemo((): HomepageGcpProject[] => {
    return orgProjects.map(p => ({
      id: `org-${p.projectId}`,
      projectId: p.projectId,
      displayName: p.displayName,
      groups: [],
      createdBy: '',
      createdAt: '',
      labels: p.labels,
      state: p.state,
      owner: p.labels?.owner || p.labels?.team,
      billing: billingData[p.projectId],
    }));
  }, [orgProjects, billingData]);

  // Select which projects to display based on view mode
  const displayProjects = useMemo(() => {
    if (viewMode === 'organization') {
      return orgProjectsAsHomepage;
    }
    // Filter by selectedGroupRefs and userSelected using UNION logic
    // Show project if: (any selected team matches) OR (user project AND userSelected)
    const noGroupsSelected = !selectedGroupRefs || selectedGroupRefs.length === 0;

    // If nothing selected, show nothing
    if (noGroupsSelected && !userSelected) {
      return [];
    }

    const filteredProjects = projects.filter(p => {
      const isUserSpecific = userRef && p.groups.some(g => g === userRef);
      const matchesUser = isUserSpecific && userSelected;
      const matchesTeam = !noGroupsSelected && p.groups.some(g => selectedGroupRefs!.includes(g));
      return matchesUser || matchesTeam;
    });

    return filteredProjects.map(p => ({
      ...p,
      billing: billingData[p.projectId],
      owner: p.labels?.owner || p.labels?.team || undefined,
    }));
  }, [viewMode, projects, orgProjectsAsHomepage, billingData, selectedGroupRefs, userSelected, userRef]);

  // Filter and sort projects
  const visibleProjects = useMemo(() => {
    let filtered = displayProjects.filter(p => !hiddenProjects.has(p.id));

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.projectId.toLowerCase().includes(query) ||
        p.displayName?.toLowerCase().includes(query) ||
        p.owner?.toLowerCase().includes(query)
      );
    }

    // Sort by selected field
    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'projectId':
          comparison = (a.displayName || a.projectId).localeCompare(b.displayName || b.projectId);
          break;
        case 'currentCost':
          comparison = (a.billing?.currentMonthCost || 0) - (b.billing?.currentMonthCost || 0);
          break;
        case 'previousCost':
          comparison = (a.billing?.previousMonthCost || 0) - (b.billing?.previousMonthCost || 0);
          break;
        case 'projectedCost':
          comparison = (a.billing?.projectedMonthCost || 0) - (b.billing?.projectedMonthCost || 0);
          break;
        case 'owner':
          comparison = (a.owner || '').localeCompare(b.owner || '');
          break;
        default:
          if (viewMode === 'saved') {
            // Default: personal projects first, then by group order
            const aIsPersonal = userRef && a.groups.includes(userRef);
            const bIsPersonal = userRef && b.groups.includes(userRef);
            if (aIsPersonal && !bIsPersonal) return -1;
            if (!aIsPersonal && bIsPersonal) return 1;

            if (groupOrder && groupOrder.length > 0) {
              const aMinOrder = Math.min(...a.groups.map(g => {
                const idx = groupOrder.indexOf(g);
                return idx === -1 ? Infinity : idx;
              }));
              const bMinOrder = Math.min(...b.groups.map(g => {
                const idx = groupOrder.indexOf(g);
                return idx === -1 ? Infinity : idx;
              }));
              comparison = aMinOrder - bMinOrder;
            }
          }
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [displayProjects, hiddenProjects, searchQuery, sortField, sortDirection, userRef, groupOrder, viewMode]);

  const handleSortChange = useCallback((field: GcpProjectSortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const formatOrgName = (org: GcpOrganization) => {
    return org.displayName || org.name.replace('organizations/', 'Org ');
  };

  if (loading) {
    return (
      <CollapsibleSection title="GCP Projects" sectionKey="gcp-projects" editMode={editMode}>
        <Progress />
      </CollapsibleSection>
    );
  }

  if (isHidden) {
    return null;
  }

  if (userGroups.length === 0 && !userRef) {
    return (
      <CollapsibleSection
        title="GCP Projects"
        sectionKey="gcp-projects"
        onHide={handleHideSection}
        editMode={editMode}
      >
        <EmptyState type="no-groups" />
      </CollapsibleSection>
    );
  }

  const renderOrgModeContent = () => {
    if (needsGoogleAuth) {
      return (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleGoogleSignIn}>
              Sign in with Google
            </Button>
          }
        >
          Sign in with Google to view all projects in your organization.
        </Alert>
      );
    }

    if (loadingOrgs) {
      return (
        <Box className={classes.loadingOrgProjects}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading organizations...</Typography>
        </Box>
      );
    }

    if (organizations.length === 0) {
      return (
        <Alert severity="warning">
          No organizations found. Make sure your Google account has access to at least one GCP organization.
        </Alert>
      );
    }

    return (
      <>
        <Box className={classes.orgSelector}>
          <FormControl variant="outlined" size="small" className={classes.orgControl}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={selectedOrgId}
              onChange={(e) => handleOrgChange(e.target.value as string)}
              label="Organization"
              startAdornment={<BusinessIcon fontSize="small" style={{ marginRight: 4 }} />}
            >
              {organizations.map(org => (
                <MenuItem key={org.name} value={org.name}>
                  {formatOrgName(org)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {loadingOrgProjects && (
            <Box className={classes.orgInfo}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading projects...</Typography>
            </Box>
          )}
          {!loadingOrgProjects && selectedOrgId && (
            <Typography variant="caption" className={classes.orgInfo}>
              {orgProjects.length} projects in organization
            </Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      <CollapsibleSection
        title="GCP Projects"
        sectionKey="gcp-projects"
        defaultCollapsed={defaultCollapsed}
        onHide={handleHideSection}
        onReorder={viewMode === 'saved' ? () => setReorderDialogOpen(true) : undefined}
        editMode={editMode}
        action={
          viewMode === 'saved' ? (
            <>
              <Button
                size="small"
                color="primary"
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={e => {
                  e.stopPropagation();
                  setAddMenuAnchor(e.currentTarget);
                }}
                className={classes.addButton}
              >
                Add
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                onClick={e => e.stopPropagation()}
              >
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(null);
                    setDialogOpen(true);
                  }}
                >
                  <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                  Add project manually
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAddMenuAnchor(null);
                    setOrgBrowserOpen(true);
                  }}
                >
                  <BusinessIcon fontSize="small" style={{ marginRight: 8 }} />
                  Browse organization projects
                </MenuItem>
              </Menu>
            </>
          ) : undefined
        }
      >
        {/* View mode tabs */}
        <Tabs
          value={viewMode}
          onChange={handleViewModeChange}
          className={classes.viewModeTabs}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value="saved"
            label="Saved Projects"
            icon={<FolderSpecialIcon />}
            className={classes.tab}
          />
          <Tab
            value="organization"
            label="All Org Projects"
            icon={<BusinessIcon />}
            className={classes.tab}
          />
          <Tab
            value="hierarchy"
            label="Hierarchy"
            icon={<AccountTreeIcon />}
            className={classes.tab}
          />
        </Tabs>

        {/* Org mode selector */}
        {viewMode === 'organization' && renderOrgModeContent()}

        {/* Hierarchy view - independent of team filtering */}
        {viewMode === 'hierarchy' && (
          <GcpHierarchyView
            googleAccessToken={googleToken}
            showOrgSelector={true}
            compact={true}
            treeOnly={true}
          />
        )}

        {/* Show toolbar and list only when we have content to show */}
        {(viewMode === 'saved' || (viewMode === 'organization' && selectedOrgId && !needsGoogleAuth)) && (
          <>
            {/* Toolbar with sorting and filtering */}
            <Box className={classes.toolbar}>
              <TextField
                className={classes.searchField}
                size="small"
                variant="outlined"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl variant="outlined" size="small" className={classes.sortControl}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortField}
                  onChange={(e) => handleSortChange(e.target.value as GcpProjectSortField)}
                  label="Sort by"
                  startAdornment={<SortIcon fontSize="small" style={{ marginRight: 4 }} />}
                >
                  <MenuItem value="projectId">Project Name</MenuItem>
                  <MenuItem value="currentCost">Current Cost</MenuItem>
                  <MenuItem value="previousCost">Previous Cost</MenuItem>
                  <MenuItem value="projectedCost">Projected Cost</MenuItem>
                  <MenuItem value="owner">Owner</MenuItem>
                </Select>
              </FormControl>

              <Tooltip title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                >
                  {sortDirection === 'asc' ? '↑ Asc' : '↓ Desc'}
                </Button>
              </Tooltip>

              {loadingBilling && (
                <Typography variant="caption" color="textSecondary">
                  Loading billing...
                </Typography>
              )}
            </Box>

            {loadingOrgProjects ? (
              <Box className={classes.loadingOrgProjects}>
                <CircularProgress size={24} />
                <Typography variant="body2">Loading organization projects...</Typography>
              </Box>
            ) : visibleProjects.length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
                No Matching GCP Projects Found
              </Typography>
            ) : (
              <List dense>
                {visibleProjects.map(project => (
                  <GcpProjectItem
                    key={project.id}
                    project={project}
                    editMode={editMode && viewMode === 'saved'}
                    onHide={viewMode === 'saved' ? handleHideProject : undefined}
                    onEditGroups={viewMode === 'saved' ? handleEditGroups : undefined}
                    onDelete={viewMode === 'saved' ? async (p) => {
                      await handleDeleteProject(p.id);
                    } : undefined}
                    userGroups={userGroups}
                    isOrgView={viewMode === 'organization'}
                  />
                ))}
              </List>
            )}
          </>
        )}
      </CollapsibleSection>

      <GcpProjectContributionDialog
        open={dialogOpen || Boolean(editingProject)}
        onClose={handleDialogClose}
        onSubmit={handleAddProject}
        userGroups={userGroups}
        userRef={userRef}
        editingProject={editingProject}
        onDelete={handleDeleteProject}
      />

      <ReorderDialog
        open={reorderDialogOpen}
        onClose={() => setReorderDialogOpen(false)}
        onSave={handleSaveGroupOrder}
        groups={groupsWithProjects}
        userRef={hasPersonalProjects ? userRef : undefined}
        currentOrder={groupOrder}
      />

      <OrgProjectBrowserDialog
        open={orgBrowserOpen}
        onClose={() => setOrgBrowserOpen(false)}
        onAddProjects={handleAddProjectsFromOrg}
        userGroups={userGroups}
        userRef={userRef}
        existingProjectIds={projects.map(p => p.projectId)}
      />
    </>
  );
};
